import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ETAPAS, COR_ORIGEM, etapasPadraoDaLista } from '../constantes';

// Listagem das negociações em tabela densa. Serve a dois donos:
//
//   1. o botão ☰ Lista do CRM, que passa as oportunidades JÁ filtradas pelo
//      quadro (origem, closer, favoritas, busca) em `oportunidades`;
//   2. o atalho Alt+L, montado no Layout e disponível em qualquer tela, que
//      não recebe nada e busca a base inteira sozinho ao abrir.
//
// Por isso `oportunidades` é opcional: sem ela o componente se vira.

const COLUNAS = [
  { campo: 'favorita',       titulo: '★',          largura: 26 },
  { campo: 'nome_cliente',   titulo: 'Cliente',    largura: 200 },
  { campo: 'telefone',       titulo: 'Telefone',   largura: 148 },
  { campo: 'etapa',          titulo: 'Etapa',      largura: 96 },
  { campo: 'origem_lead',    titulo: 'Origem',     largura: 100 },
  { campo: 'origem_detalhe', titulo: 'Detalhe',    largura: 150 },
  { campo: 'closer',         titulo: 'Closer',     largura: 112 },
  { campo: 'sdr',            titulo: 'SDR',        largura: 104 },
  { campo: 'parceiro_nome',  titulo: 'Parceiro',   largura: 112 },
  { campo: 'criado_em',      titulo: 'Criada em',  largura: 78 },
  { campo: 'observacao',     titulo: 'Observação', largura: 300 },
];
const th = { position: 'sticky', top: 0, zIndex: 1, background: '#0a1628', color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', padding: '5px 6px', textAlign: 'left', cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' };
const td = { padding: '3px 6px', fontSize: 11.5, borderBottom: '1px solid #eef1f5', verticalAlign: 'top', lineHeight: 1.35 };
const chip = { display: 'inline-block', padding: '0 5px', borderRadius: 3, fontSize: 10, fontWeight: 700, lineHeight: '15px', whiteSpace: 'nowrap' };
const btnAtalho = { background: '#fff', color: '#56616f', border: '1px solid #dde2e9', borderRadius: 3, padding: '2px 8px', fontSize: 11, cursor: 'pointer' };
const vazio = <span style={{ color: '#b6bdc7' }}>—</span>;

const token = () => localStorage.getItem('token');
const so8 = (t) => String(t || '').replace(/\D/g, '');

// Copia só os dígitos, igual ao botão 📋 do card do CRM. Nem todo navegador dá
// clipboard fora de https, então o textarea escondido fica de reserva.
function copiarTexto(txt) {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(txt).catch(() => copiaAntiga(txt));
    return;
  }
  copiaAntiga(txt);
}
function copiaAntiga(txt) {
  const el = document.createElement('textarea');
  el.value = txt;
  el.style.cssText = 'position:fixed;top:0;left:0;opacity:0';
  document.body.appendChild(el);
  el.select();
  try { document.execCommand('copy'); } catch (e) { /* sem clipboard: desiste calado */ }
  document.body.removeChild(el);
}

function dataCurta(v) {
  const d = new Date(v || 0);
  return !v || Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

export default function ListagemNegociacoes({
  aberto,
  onFechar,
  oportunidades,     // opcional: quando vem do CRM, já filtradas
  totalBase,         // opcional: total sem filtro, só pro rótulo "N de M"
  onLinha,           // opcional: o que fazer ao clicar na linha
  onFavorita,        // opcional: deixa o dono sincronizar a estrela
  onMoverEtapa,      // opcional: idem, pra mudança de etapa (o CRM já tem a dele)
}) {
  const navigate = useNavigate();
  const controlada = Array.isArray(oportunidades);
  const [proprias, setProprias] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [ordem, setOrdem] = useState({ campo: 'criado_em', desc: true });
  const [etapasLigadas, setEtapasLigadas] = useState(etapasPadraoDaLista);
  const [copiado, setCopiado] = useState('');   // _id da linha que acabou de copiar
  const [salvandoEtapa, setSalvandoEtapa] = useState('');
  // Pelo Alt+L a listagem nasce com as negociações de quem está logado — quem
  // abre quer ver as suas. No modo controlado quem manda é o filtro do quadro,
  // e este nem aparece: dois filtros de closer na tela se anulariam em silêncio.
  const [filtroCloser, setFiltroCloser] = useState(() => localStorage.getItem('nomeUsuario') || '');

  // Modo atalho: busca a base ao abrir (e só então, pra não pesar as telas)
  useEffect(() => {
    if (!aberto || controlada) return;
    let vivo = true;
    setCarregando(true);
    setErro('');
    fetch('/oportunidades', { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error('Não consegui carregar as negociações.'))))
      .then(d => {
        if (!vivo) return;
        const lista = Array.isArray(d) ? d : [];
        setProprias(lista);
        // Quem não é closer de nada (SDR, operacional) abriria numa tabela
        // vazia sem entender por quê — nesse caso começa com todos.
        setFiltroCloser(f => (f && !lista.some(o => o.closer === f) ? '' : f));
      })
      .catch(e => { if (vivo) setErro(e.message); })
      .finally(() => { if (vivo) setCarregando(false); });
    return () => { vivo = false; };
  }, [aberto, controlada]);

  // Esc fecha
  useEffect(() => {
    if (!aberto) return undefined;
    const aoTeclar = (e) => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [aberto, onFechar]);

  const marcarFavorita = useCallback(async (op) => {
    const nova = !op.favorita;
    if (!controlada) setProprias(l => l.map(x => (x._id === op._id ? { ...x, favorita: nova } : x)));
    if (onFavorita) { onFavorita(op); return; }
    await fetch(`/oportunidades/${op._id}/favorita`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
      body: JSON.stringify({ favorita: nova }),
    }).catch(() => {});
  }, [controlada, onFavorita]);

  // Mudar a etapa direto na lista. Se a etapa nova estiver desligada nos chips
  // lá em cima, a linha some da tabela — é o filtro fazendo o trabalho dele, e
  // o contador do chip sobe pra mostrar pra onde ela foi.
  const mudarEtapa = async (op, nova) => {
    if (!nova || nova === op.etapa) return;
    setSalvandoEtapa(op._id);
    try {
      if (onMoverEtapa) {
        await onMoverEtapa(op, nova);
      } else {
        const res = await fetch(`/oportunidades/${op._id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
          body: JSON.stringify({ ...op, etapa: nova }),
        });
        const corpo = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(corpo.erro || 'Não consegui mudar a etapa.');
        setProprias(l => l.map(x => (x._id === op._id ? { ...x, ...corpo, etapa: nova } : x)));
      }
    } catch (e) {
      alert(e.message);
    } finally {
      setSalvandoEtapa('');
    }
  };

  if (!aberto) return null;

  const SEM_CLOSER = '(sem closer)';
  const closersNaBase = controlada ? [] : [...new Set(proprias.map(o => o.closer).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  const temSemCloser = !controlada && proprias.some(o => !o.closer);

  const fonte = controlada ? oportunidades : proprias.filter(op => {
    if (!filtroCloser) return true;
    return filtroCloser === SEM_CLOSER ? !op.closer : op.closer === filtroCloser;
  });
  const total = totalBase != null ? totalBase : (controlada ? fonte.length : proprias.length);

  const contagem = fonte.reduce((acc, op) => {
    acc[op.etapa] = (acc[op.etapa] || 0) + 1;
    return acc;
  }, {});

  const valorDa = (op, campo) => {
    if (campo === 'criado_em') return new Date(op.criado_em || 0).getTime() || 0;
    if (campo === 'etapa') return ETAPAS.findIndex(e => e.key === op.etapa);
    if (campo === 'favorita') return op.favorita ? 1 : 0;
    if (campo === 'telefone') return so8(op.telefone);
    return String(op[campo] || '').toLowerCase();
  };
  const linhas = fonte.filter(op => etapasLigadas.includes(op.etapa)).sort((a, b) => {
    const va = valorDa(a, ordem.campo);
    const vb = valorDa(b, ordem.campo);
    const cmp = typeof va === 'number' && typeof vb === 'number'
      ? va - vb
      : String(va).localeCompare(String(vb), 'pt-BR');
    return ordem.desc ? -cmp : cmp;
  });

  const ordenarPor = (campo) => setOrdem(o => (
    o.campo === campo ? { campo, desc: !o.desc } : { campo, desc: campo === 'criado_em' }
  ));
  const alternarEtapa = (key) => setEtapasLigadas(l => (
    l.includes(key) ? l.filter(k => k !== key) : [...l, key]
  ));
  const abrirLinha = (op) => {
    if (onLinha) { onLinha(op); return; }
    navigate(`/crm?oportunidade=${op._id}`);
    onFechar();
  };

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onFechar(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 3000, padding: 14, display: 'flex' }}
    >
      <div style={{ background: '#fff', borderRadius: 4, border: '1px solid #c8d0da', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ background: '#0a1628', padding: '7px 12px', borderRadius: '4px 4px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          <span style={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>
            ☰ Listagem
            <span style={{ fontWeight: 400, opacity: 0.75, marginLeft: 8, fontSize: 12 }}>
              {carregando ? 'carregando...'
                : linhas.length === total ? `${linhas.length} negociações`
                  : `${linhas.length} de ${total}${controlada ? ' — os filtros do quadro valem aqui' : ''}`}
            </span>
          </span>
          <span style={{ color: '#fff', fontSize: 11, opacity: 0.7 }}>clique no cabeçalho para ordenar · na linha para abrir</span>
          <button onClick={onFechar} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
        </div>

        {/* Etapas: abre só com o que está em andamento; clicar traz de volta */}
        <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap', padding: '6px 10px', borderBottom: '1px solid #e6ebf1', background: '#f7f9fb', flexShrink: 0 }}>
          <span style={{ fontSize: 10.5, fontWeight: 700, color: '#828d9c', letterSpacing: '.05em', textTransform: 'uppercase', marginRight: 2 }}>Etapas</span>
          {ETAPAS.map(e => {
            const ligada = etapasLigadas.includes(e.key);
            return (
              <button key={e.key} onClick={() => alternarEtapa(e.key)}
                title={ligada ? `Esconder ${e.label} da listagem` : `Mostrar ${e.label} na listagem`}
                style={{
                  background: ligada ? e.bg : '#fff',
                  color: ligada ? e.cor : '#a8b1bd',
                  border: `1px solid ${ligada ? e.cor : '#dde2e9'}`,
                  borderRadius: 3, padding: '2px 8px', fontSize: 11,
                  fontWeight: ligada ? 700 : 500, cursor: 'pointer', whiteSpace: 'nowrap',
                }}>
                {ligada ? '' : '＋ '}{e.label} <span style={{ opacity: 0.65, fontWeight: 500 }}>{contagem[e.key] || 0}</span>
              </button>
            );
          })}
          <span style={{ flex: 1 }} />
          {!controlada && (
            <select
              value={filtroCloser}
              onChange={e => setFiltroCloser(e.target.value)}
              title="De quem são as negociações que aparecem"
              style={{
                ...btnAtalho, padding: '2px 6px', maxWidth: 190,
                color: filtroCloser ? '#0a1628' : '#56616f',
                fontWeight: filtroCloser ? 700 : 400,
                borderColor: filtroCloser ? '#b0b8c4' : '#dde2e9',
              }}
            >
              <option value="">Todos os closers</option>
              {closersNaBase.map(c => <option key={c} value={c}>{c}</option>)}
              {temSemCloser && <option value={SEM_CLOSER}>{SEM_CLOSER}</option>}
            </select>
          )}
          <button onClick={() => setEtapasLigadas(ETAPAS.map(e => e.key))} style={btnAtalho}>todas</button>
          <button onClick={() => setEtapasLigadas([])} title="Desliga todas as etapas — aí é só ligar as que quiser ver" style={btnAtalho}>nenhuma</button>
          <button onClick={() => setEtapasLigadas(etapasPadraoDaLista())} title="Volta ao padrão: sem ganho, perdido e sem interação" style={btnAtalho}>só em andamento</button>
        </div>

        <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed' }}>
            <colgroup>{COLUNAS.map(c => <col key={c.campo} style={{ width: c.largura }} />)}</colgroup>
            <thead>
              <tr>
                {COLUNAS.map(c => (
                  <th key={c.campo} style={th} onClick={() => ordenarPor(c.campo)} title={`Ordenar por ${c.titulo}`}>
                    {c.titulo}{ordem.campo === c.campo ? (ordem.desc ? ' ▾' : ' ▴') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {linhas.map((op, i) => {
                const etapa = ETAPAS.find(e => e.key === op.etapa) || ETAPAS[0];
                const corOrigem = COR_ORIGEM[op.origem_lead] || '#6b7280';
                const fundo = op.favorita ? '#fffbeb' : i % 2 ? '#fafbfc' : '#fff';
                return (
                  <tr key={op._id}
                    onClick={() => abrirLinha(op)}
                    style={{ background: fundo, cursor: 'pointer' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#eef4fb'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = fundo; }}
                  >
                    <td style={{ ...td, textAlign: 'center' }}>
                      <button onClick={e => { e.stopPropagation(); marcarFavorita(op); }}
                        title={op.favorita ? 'Tirar dos favoritos' : 'Marcar como favorita'}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, padding: 0, lineHeight: 1, opacity: op.favorita ? 1 : 0.35 }}>
                        {op.favorita ? '⭐' : '☆'}
                      </button>
                    </td>
                    <td style={{ ...td, fontWeight: 700, color: '#0a1628' }}>{op.nome_cliente}</td>
                    <td style={{ ...td, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                      {op.telefone ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span
                            onClick={e => { e.stopPropagation(); window.open(`https://web.whatsapp.com/send?phone=${so8(op.whatsapp_tel || op.telefone)}`, '_blank'); }}
                            title="Abrir WhatsApp"
                            style={{ color: '#128c3e', cursor: 'pointer', fontWeight: 600 }}
                          >{op.telefone}</span>
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              copiarTexto(so8(op.telefone));
                              setCopiado(op._id);
                              setTimeout(() => setCopiado(c => (c === op._id ? '' : c)), 1200);
                            }}
                            title="Copiar número"
                            style={{
                              background: copiado === op._id ? '#dcfce7' : '#f3f4f6',
                              border: `1px solid ${copiado === op._id ? '#86efac' : '#d1d5db'}`,
                              borderRadius: 3, padding: '0 4px', fontSize: 9.5, lineHeight: '15px',
                              cursor: 'pointer', flexShrink: 0,
                            }}>
                            {copiado === op._id ? '✓' : '📋'}
                          </button>
                        </span>
                      ) : vazio}
                    </td>
                    <td style={td} onClick={e => e.stopPropagation()}>
                      {/* select nativo em vez de menu próprio: abre por cima do
                          scroll da tabela sem ser cortado, e funciona no teclado */}
                      <select
                        value={op.etapa}
                        disabled={salvandoEtapa === op._id}
                        onChange={e => mudarEtapa(op, e.target.value)}
                        title="Mudar a etapa desta negociação"
                        style={{
                          ...chip,
                          appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
                          background: etapa.bg, color: etapa.cor, border: `1px solid ${etapa.cor}44`,
                          font: 'inherit', fontSize: 10, fontWeight: 700, height: 17,
                          padding: '0 4px', maxWidth: '100%', cursor: 'pointer',
                          opacity: salvandoEtapa === op._id ? 0.5 : 1,
                        }}
                      >
                        {ETAPAS.map(e => (
                          <option key={e.key} value={e.key} style={{ background: '#fff', color: '#16202e', fontWeight: 400 }}>
                            {e.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td style={td}>
                      {op.origem_lead
                        ? <span style={{ ...chip, background: `${corOrigem}18`, color: corOrigem, border: `1px solid ${corOrigem}44` }}>{op.origem_lead}</span>
                        : vazio}
                    </td>
                    {['origem_detalhe', 'closer', 'sdr', 'parceiro_nome'].map(campo => (
                      <td key={campo} style={{ ...td, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={op[campo] || ''}>
                        {op[campo] || vazio}
                      </td>
                    ))}
                    <td style={{ ...td, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', color: '#56616f' }}>{dataCurta(op.criado_em)}</td>
                    <td style={{ ...td, color: '#56616f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={op.observacao || ''}>
                      {op.observacao || vazio}
                    </td>
                  </tr>
                );
              })}
              {linhas.length === 0 && (
                <tr><td colSpan={COLUNAS.length} style={{ ...td, textAlign: 'center', padding: 30, color: erro ? '#b91c1c' : '#6b7280' }}>
                  {carregando ? 'Carregando...'
                    : erro ? erro
                      : etapasLigadas.length === 0 ? 'Nenhuma etapa selecionada — ligue pelo menos uma ali em cima.'
                        : 'Nenhuma negociação com os filtros de agora.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
